import { ErrorHandler, Injectable } from '@angular/core';

import { consolaOriginal, depuracionActiva, mandarInformeDeError } from './informe-de-errores';

/**
 * Un trozo de la aplicación que ya no existe (12/09/2026). Cada despliegue renombra los
 * ficheros con un hash nuevo; una pestaña abierta desde antes sigue pidiendo los suyos y
 * recibe 404 al navegar a una ruta perezosa: la página se queda a medias sin decir nada
 * (57 respuestas así en el registro de nginx del 12/09, repartidas por todas las horas).
 *
 * La respuesta correcta es recargar: el HTML nuevo trae los nombres nuevos. UNA sola vez
 * por pestaña -bandera en sessionStorage- para que un fallo real (sin red, un bloqueador)
 * no se convierta en un bucle de recargas.
 *
 * Hace falta en dos sitios: los eventos de ventana (main.ts) cubren los import dinámicos
 * sueltos, y este ErrorHandler cubre los de Angular, que atrapa los fallos de ruta
 * perezosa en su propio manejador y nunca llegan a la ventana. En producción, además,
 * `console.error` está silenciado, así que sin esto el fallo no deja ni rastro.
 */
export const MARCA_RECARGA = 'mc-recarga-por-trozo';

export function esTrozoPerdido(motivo: unknown): boolean {
  const error = motivo as { message?: string; name?: string; rejection?: unknown } | null;
  const texto = String(error?.message ?? error?.name ?? motivo ?? '');
  return /ChunkLoadError|Loading chunk|dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(texto);
}

/** Devuelve true si ha pedido la recarga (útil para probarlo sin recargar de verdad). */
export function recargarSiFaltaUnTrozo(
  motivo: unknown,
  recargar: () => void = () => location.reload(),
  almacen: Pick<Storage, 'getItem' | 'setItem'> | null = typeof sessionStorage === 'undefined' ? null : sessionStorage,
): boolean {
  if (!esTrozoPerdido(motivo)) return false;

  try {
    if (almacen?.getItem(MARCA_RECARGA)) return false;
    almacen?.setItem(MARCA_RECARGA, String(Date.now()));
  } catch {
    // Sin sessionStorage (modo privado estricto): se recarga igual. Una vez basta para el
    // caso normal, y dejar la página inservible es peor que el riesgo de una repetición.
  }

  recargar();
  return true;
}

@Injectable()
export class ManejadorDeErrores extends ErrorHandler {
  override handleError(error: unknown): void {
    // Un trozo que falta no es una avería: es una pestaña vieja. Se recarga y ya está, sin
    // gastar en ello uno de los cinco informes que admite la sesión.
    if (recargarSiFaltaUnTrozo(error)) return;

    // Antes de delegar, porque `super.handleError` acaba en el `console.error` anulado
    // (main.ts y el APP_INITIALIZER de app.module.ts): a partir de esa llamada el error ya
    // no existe para nadie. Ver informe-de-errores.ts para el muestreo y el tope.
    mandarInformeDeError(error);

    // Con el interruptor puesto, además, el error entero por la consola de verdad: en
    // producción el resumen que viaja va recortado y tachado a propósito.
    if (depuracionActiva()) consolaOriginal(error);

    super.handleError(error);
  }
}
